import { useEffect, useState } from "react";
import { useEditMode, SectionConfig } from "@/contexts/EditModeContext";
import { cmsFields } from "@/pages/admin/cms/cmsFieldsConfig";
import CmsFieldRenderer from "@/pages/admin/cms/CmsFieldRenderer";
import LocaleTabs, { LocaleCode, storageKeyFor } from "@/components/admin/LocaleTabs";
import { supabase } from "@/integrations/supabase/client";
import { translateFields } from "@/lib/cms-translate";
import { useToast } from "@/hooks/use-toast";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { GripVertical, Eye, EyeOff, ArrowUp, ArrowDown, Sparkles } from "lucide-react";

const EditSidePanel = () => {
  const { panelOpen, setPanelOpen, selectedSection, selectSection, sectionOrder, saveSectionOrder } = useEditMode();
  const [locale, setLocale] = useState<LocaleCode>("pt-PT");
  const [translating, setTranslating] = useState(false);
  const { toast } = useToast();
  const [values, setValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [tab, setTab] = useState<"fields" | "layout">("fields");

  // Load CMS values
  useEffect(() => {
    supabase.from("app_settings").select("key, value").then(({ data }) => {
      const map: Record<string, string> = {};
      (data || []).forEach((row: any) => {
        let val = row.value;
        if (typeof val === "string") {
          try { val = JSON.parse(val); } catch {}
        }
        map[row.key] = typeof val === "string" ? val : JSON.stringify(val);
      });
      setValues(map);
    });
  }, []);

  const sectionConfig = sectionOrder.find((s) => s.id === selectedSection);
  const groupName = sectionConfig?.group;
  const fields = groupName ? cmsFields.filter((f) => f.group === groupName) : [];

  const autoTranslateBase = async (baseKey: string, ptValue: string) => {
    const field = cmsFields.find((f) => f.key === baseKey);
    if (!field || (field.type !== "text" && field.type !== "textarea")) return;
    if (!ptValue.trim()) return;
    await translateFields([{ key: baseKey, value: ptValue }], {
      existing: values,
      onValue: (sKey, val) => setValues((p) => ({ ...p, [sKey]: val })),
    });
  };


  const handleSave = async (key: string) => {
    setSaving((p) => ({ ...p, [key]: true }));
    const val = values[key] ?? "";
    const { error } = await supabase
      .from("app_settings")
      .upsert({ key, value: val }, { onConflict: "key" });
    if (error) {
      toast({ title: "Erro", description: error.message, variant: "destructive" });
    } else if (!key.includes("__")) {
      autoTranslateBase(key, val);
    }
    setSaving((p) => ({ ...p, [key]: false }));
  };

  const handleImageUpload = async (key: string, file: File) => {
    setUploading((p) => ({ ...p, [key]: true }));
    const ext = file.name.split(".").pop();
    const path = `${key}-${Date.now()}.${ext}`;
    const { error: uploadError } = await supabase.storage
      .from("cms-images")
      .upload(path, file, { upsert: true });
    if (uploadError) {
      toast({ title: "Erro no upload", description: uploadError.message, variant: "destructive" });
      setUploading((p) => ({ ...p, [key]: false }));
      return;
    }
    const { data: urlData } = supabase.storage.from("cms-images").getPublicUrl(path);
    const url = urlData.publicUrl;
    setValues((p) => ({ ...p, [key]: url }));
    await supabase.from("app_settings").upsert({ key, value: url }, { onConflict: "key" });
    toast({ title: "Imagem carregada!" });
    setUploading((p) => ({ ...p, [key]: false }));
  };

  const handleRemoveImage = async (key: string) => {
    setValues((p) => ({ ...p, [key]: "" }));
    await supabase.from("app_settings").upsert({ key, value: "" }, { onConflict: "key" });
    toast({ title: "Imagem removida!" });
  };

  const handleChange = (key: string, value: string) => {
    setValues((p) => ({ ...p, [key]: value }));
  };

  const moveSection = (index: number, direction: "up" | "down") => {
    const newOrder = [...sectionOrder];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newOrder.length) return;
    [newOrder[index], newOrder[targetIndex]] = [newOrder[targetIndex], newOrder[index]];
    saveSectionOrder(newOrder);
  };

  const toggleVisibility = (index: number) => {
    const newOrder = [...sectionOrder];
    newOrder[index] = { ...newOrder[index], visible: !newOrder[index].visible };
    saveSectionOrder(newOrder);
  };

  return (
    <Sheet open={panelOpen} onOpenChange={(open) => {
      setPanelOpen(open);
      if (!open) selectSection(null);
    }}>
      <SheetContent className="w-[400px] sm:w-[450px] overflow-y-auto" side="right">
        <SheetHeader className="pb-4">
          <SheetTitle className="font-display text-xl">
            {selectedSection ? `Editar: ${sectionConfig?.label || selectedSection}` : "Editor do Site"}
          </SheetTitle>
        </SheetHeader>

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-muted rounded-xl mb-6">
          <button
            onClick={() => setTab("fields")}
            className={`flex-1 py-2 px-3 text-sm font-medium rounded-lg transition-colors ${
              tab === "fields" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Conteúdo
          </button>
          <button
            onClick={() => setTab("layout")}
            className={`flex-1 py-2 px-3 text-sm font-medium rounded-lg transition-colors ${
              tab === "layout" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Layout
          </button>
        </div>

        {tab === "fields" && selectedSection && (
          <div className="space-y-5">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <LocaleTabs value={locale} onChange={setLocale} />
              {locale !== "pt-PT" && groupName && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={translating}
                  onClick={async () => {
                    setTranslating(true);
                    const texts = fields
                      .filter((f) => f.type === "text" || f.type === "textarea")
                      .map((f) => ({ key: f.key, value: values[f.key] || "" }))
                      .filter((t) => t.value.trim());
                    if (!texts.length) { setTranslating(false); return; }
                    try {
                      const { data, error } = await supabase.functions.invoke("translate", {
                        body: { texts, targetLang: locale },
                      });
                      if (error) throw error;
                      const translated: { key: string; value: string }[] = data?.translated || [];
                      const updates = translated.map((t) => ({
                        key: storageKeyFor(t.key, locale),
                        value: t.value,
                      }));
                      if (updates.length) {
                        await supabase.from("app_settings").upsert(updates, { onConflict: "key" });
                        setValues((p) => {
                          const next = { ...p };
                          updates.forEach((u, i) => { next[u.key] = translated[i].value; });
                          return next;
                        });
                      }
                      toast({ title: `Traduzido ${translated.length} campos ✓` });
                    } catch (e: any) {
                      toast({ title: "Erro na tradução", description: e.message, variant: "destructive" });
                    } finally {
                      setTranslating(false);
                    }
                  }}
                  className="gap-1"
                >
                  <Sparkles size={14} /> {translating ? "…" : "Traduzir"}
                </Button>
              )}
            </div>
            {fields.length === 0 ? (
              <p className="text-muted-foreground text-sm">Nenhum campo editável para esta secção.</p>
            ) : (
              fields.map((field) => {
                const sKey = storageKeyFor(field.key, locale);
                return (
                  <CmsFieldRenderer
                    key={sKey}
                    field={field}
                    storageKey={sKey}
                    baseValue={locale !== "pt-PT" ? values[field.key] : undefined}
                    value={values[sKey] || ""}
                    saving={!!saving[sKey]}
                    uploading={!!uploading[sKey]}
                    onChange={handleChange}
                    onSave={handleSave}
                    onImageUpload={handleImageUpload}
                    onRemoveImage={handleRemoveImage}
                  />
                );
              })
            )}
          </div>
        )}

        {tab === "fields" && !selectedSection && (
          <p className="text-muted-foreground text-sm">Clique numa secção no site para editar o seu conteúdo.</p>
        )}

        {tab === "layout" && (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground mb-4">
              Reordene as secções e controle a visibilidade de cada uma.
            </p>
            {sectionOrder.map((section, index) => (
              <div
                key={section.id}
                className="flex items-center gap-2 p-3 rounded-xl border border-border bg-card"
              >
                <GripVertical size={16} className="text-muted-foreground flex-shrink-0" />
                <span className="text-sm font-medium flex-1">{section.label}</span>
                <button
                  onClick={() => toggleVisibility(index)}
                  className="p-1 rounded hover:bg-muted transition-colors"
                  title={section.visible ? "Ocultar" : "Mostrar"}
                >
                  {section.visible ? <Eye size={14} /> : <EyeOff size={14} className="text-muted-foreground" />}
                </button>
                <button
                  onClick={() => moveSection(index, "up")}
                  disabled={index === 0}
                  className="p-1 rounded hover:bg-muted transition-colors disabled:opacity-30"
                >
                  <ArrowUp size={14} />
                </button>
                <button
                  onClick={() => moveSection(index, "down")}
                  disabled={index === sectionOrder.length - 1}
                  className="p-1 rounded hover:bg-muted transition-colors disabled:opacity-30"
                >
                  <ArrowDown size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default EditSidePanel;
